import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  EnvelopeSimple as EnvelopeIcon,
  Lock as LockIcon,
  Paperclip as PaperclipIcon,
  CalendarBlank as CalendarBlankIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  Trash as TrashIcon,
  Warning as WarningIcon,
  X as XIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import axios from 'axios';

import type { Announcement } from '@/types/announcement';
import { Announcement_Type } from '@/types/announcement';
import { config } from '@/config';
import { paths } from '@/paths';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { deleteAnnouncement } from '@/api/announcements';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import AnnouncementAttachmentsDialog from '@/pages/announcements/dialogs/announcement-attachments-dialog';

// ─── Section label ─────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.disabled',
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

// ─── KV row ────────────────────────────────────────────────────────────────

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ py: 1.25, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', flexShrink: 0, minWidth: 100 }} variant="body2">
        {label}
      </Typography>
      <Box sx={{ fontSize: '0.8125rem', fontWeight: 500, textAlign: 'right' }}>
        {children}
      </Box>
    </Stack>
  );
}

// ─── Main message body ─────────────────────────────────────────────────────

interface AnnouncementBodyProps {
  announcement?: Announcement | null;
  isLoading: boolean;
  onRefresh?: () => void;
}

export function AnnouncementBody({ announcement, isLoading, onRefresh }: AnnouncementBodyProps): React.JSX.Element {
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);

  const handleCancel = useCallback(async () => {
    if (!announcement) return;
    try {
      await axios.patch(
        `${config.site.api}/api/v1/announcements/${announcement.uuid ?? announcement.id}`,
        { status: 'cancelled' },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      toast.success('Announcement cancelled');
      onRefresh?.();
    } catch {
      toast.error('Failed to cancel announcement');
    }
  }, [announcement, onRefresh]);

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton height={16} width="25%" variant="text" />
        <Skeleton height={100} variant="rectangular" sx={{ borderRadius: 1 }} />
      </Stack>
    );
  }

  const isScheduled = !!announcement?.scheduled_at;
  const isFuture = isScheduled && dayjs(announcement?.scheduled_at).isAfter(dayjs());
  const fileCount = announcement?.files?.length ?? 0;

  return (
    <Box>
      <SectionLabel>Message</SectionLabel>

      {announcement?.message ? (
        <Typography
          sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: '0.9375rem', color: 'text.primary' }}
          variant="body2"
        >
          {announcement.message}
        </Typography>
      ) : (
        <Typography color="text.disabled" sx={{ fontStyle: 'italic' }} variant="body2">
          No message content.
        </Typography>
      )}

      {/* Cancel button for future scheduled announcements */}
      {isFuture && (
        <Box sx={{ mt: 3 }}>
          <Button
            color="error"
            onClick={handleCancel}
            size="small"
            startIcon={<XIcon size={14} />}
            variant="outlined"
          >
            Cancel Announcement
          </Button>
        </Box>
      )}

      {/* Attachments */}
      {fileCount > 0 && (
        <Box sx={{ mt: 3 }}>
          <SectionLabel>Attachments</SectionLabel>
          <Button
            onClick={() => setAttachmentsOpen(true)}
            size="small"
            startIcon={<PaperclipIcon size={14} />}
            variant="outlined"
          >
            View {fileCount} Attachment{fileCount !== 1 ? 's' : ''}
          </Button>
        </Box>
      )}

      {announcement?.files && (
        <AnnouncementAttachmentsDialog
          files={announcement.files as any}
          onClose={() => setAttachmentsOpen(false)}
          open={attachmentsOpen}
        />
      )}
    </Box>
  );
}

// ─── Sidebar details ───────────────────────────────────────────────────────

interface AnnouncementSidebarProps {
  announcement?: Announcement | null;
  isLoading: boolean;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Published: { bg: '#10B98118', text: '#10B981' },
  Pending:   { bg: '#F59E0B18', text: '#F59E0B' },
  Draft:     { bg: '#6B728018', text: '#6B7280' },
  Processing:{ bg: '#6366F118', text: '#6366F1' },
  Cancelled: { bg: '#EF444418', text: '#EF4444' },
  Failed:    { bg: '#EF444418', text: '#EF4444' },
};

export function AnnouncementSidebar({ announcement, isLoading }: AnnouncementSidebarProps): React.JSX.Element {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Managers, board members, and account admins see operational metadata
  // (status, targeting, delivery, scheduling). Residents only see content.
  const { hasBoardLevelAccess } = useCommunityRole();

  const handleDelete = useCallback(async () => {
    if (!announcement) return;
    const draft = String(announcement.status ?? '').toLowerCase() === 'draft';
    setIsDeleting(true);
    try {
      await deleteAnnouncement(announcement.uuid ?? String(announcement.id));
      toast.success(draft ? 'Draft deleted' : 'Announcement removed');
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      navigate(paths.announcements.index);
    } catch {
      toast.error('Failed to delete announcement');
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  }, [announcement, navigate, queryClient]);

  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={18} variant="text" />
        ))}
      </Stack>
    );
  }

  const typeLabel =
    announcement?.type != null
      ? typeof announcement.type === 'number'
        ? (Announcement_Type[announcement.type as keyof typeof Announcement_Type] as string)
        : (announcement.type as string)
      : null;

  const statusLabel = announcement?.status as string | undefined;
  const statusStyle = statusLabel ? (STATUS_COLORS[toTitleCase(statusLabel)] ?? { bg: '#6B728018', text: '#6B7280' }) : null;
  const scheduledInFuture = !!announcement?.scheduled_at && new Date(announcement.scheduled_at) > new Date();
  const isSendingStatus = ['pending', 'processing'].includes(statusLabel?.toLowerCase() ?? '') && !scheduledInFuture;

  const isScheduled = !!announcement?.scheduled_at;
  const isFuture = isScheduled && dayjs(announcement?.scheduled_at).isAfter(dayjs());
  const isDraft = String(announcement?.status ?? '').toLowerCase() === 'draft';

  return (
    <Stack divider={<Divider />} spacing={0}>
      {/* ── Sending banner ── */}
      {isSendingStatus && (
        <Alert
          icon={<CircularProgress size={16} />}
          severity="info"
          sx={{ mb: 2, fontSize: '0.8125rem', alignItems: 'center' }}
        >
          Delivering to members… this page updates automatically.
        </Alert>
      )}

      {/* ── Details ── */}
      <Box sx={{ pb: 2 }}>
        <SectionLabel>Details</SectionLabel>
        <Stack divider={<Divider flexItem />}>
          {typeLabel && (
            <KVRow label="Type">
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{toTitleCase(typeLabel)}</Typography>
            </KVRow>
          )}
          {hasBoardLevelAccess && statusLabel && statusStyle && (() => {
            // A future-scheduled announcement is "pending" but hasn't started
            // sending yet — show "Scheduled", not a "Sending…" spinner.
            const isSending = ['pending', 'processing'].includes(statusLabel.toLowerCase()) && !scheduledInFuture;
            const chipLabel = scheduledInFuture ? 'Scheduled' : isSending ? 'Sending…' : toTitleCase(statusLabel);
            return (
              <KVRow label="Status">
                <Chip
                  icon={isSending ? <CircularProgress size={10} sx={{ color: `${statusStyle.text} !important`, ml: '6px !important' }} /> : undefined}
                  label={chipLabel}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: statusStyle.bg,
                    color: statusStyle.text,
                    border: `1px solid ${statusStyle.text}40`,
                    '& .MuiChip-label': { pl: isSending ? 0.5 : undefined },
                  }}
                />
              </KVRow>
            );
          })()}
          {/* Managers see when it was drafted ("Created"); residents see when it
              actually went out ("Published"), which differs for scheduled sends. */}
          {hasBoardLevelAccess
            ? announcement?.created_at && (
                <KVRow label="Created">
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                    {dayjs(announcement.created_at).format('MMM D, YYYY, h:mm A')}
                  </Typography>
                </KVRow>
              )
            : (() => {
                const when = announcement?.processed_on ?? announcement?.created_at;
                if (!when) return null;
                return (
                  <KVRow label={announcement?.processed_on ? 'Published' : 'Posted'}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                      {dayjs(when).format('MMM D, YYYY, h:mm A')}
                    </Typography>
                  </KVRow>
                );
              })()}
          {announcement?.creator?.name && (
            <KVRow label="Created by">
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                {announcement.creator.name}
              </Typography>
            </KVRow>
          )}
          {hasBoardLevelAccess && (
            <KVRow label="Visibility">
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                {announcement?.board_members_only ? (
                  <>
                    <LockIcon color="var(--mui-palette-primary-main)" size={14} />
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Board Members Only</Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>All Members</Typography>
                )}
              </Stack>
            </KVRow>
          )}
        </Stack>
      </Box>

      {/* ── Delivery (managers only) ── */}
      {hasBoardLevelAccess && (
      <Box sx={{ py: 2 }}>
        <SectionLabel>Delivery</SectionLabel>
        <Stack divider={<Divider flexItem />}>
          <KVRow label="Email">
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
              <EnvelopeIcon
                size={14}
                color={announcement?.send_email ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-text-disabled)'}
              />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                {announcement?.send_email ? 'Enabled' : 'Disabled'}
              </Typography>
            </Stack>
          </KVRow>
        </Stack>
      </Box>
      )}

      {/* ── Schedule (managers only) ── */}
      {hasBoardLevelAccess && isScheduled && (
        <Box sx={{ py: 2 }}>
          <SectionLabel>Schedule</SectionLabel>
          <Stack divider={<Divider flexItem />}>
            <KVRow label={isFuture ? 'Scheduled for' : 'Scheduled at'}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                <CalendarBlankIcon
                  size={14}
                  color={isFuture ? 'var(--mui-palette-warning-main)' : 'var(--mui-palette-text-disabled)'}
                />
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                  {dayjs(announcement.scheduled_at).format('MMM D, YYYY, h:mm A')}
                </Typography>
              </Stack>
            </KVRow>
            {announcement.processed_on && (
              <KVRow label="Delivered on">
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                  <CheckCircleIcon size={14} color="var(--mui-palette-success-main)" />
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                    {dayjs(announcement.processed_on).format('MMM D, YYYY, h:mm A')}
                  </Typography>
                </Stack>
              </KVRow>
            )}
          </Stack>
        </Box>
      )}

      {/* Delivered (non-scheduled, managers only) */}
      {hasBoardLevelAccess && !isScheduled && announcement?.processed_on && (
        <Box sx={{ py: 2 }}>
          <SectionLabel>Delivered</SectionLabel>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <CheckCircleIcon size={14} color="var(--mui-palette-success-main)" />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              {dayjs(announcement.processed_on).format('MMM D, YYYY, h:mm A')}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── Danger zone — community managers, board members, and account admins ── */}
      <RoleGuard
        requiredAccountRoles={[
          ACCOUNT_ROLES.ACCOUNT_OWNER,
          ACCOUNT_ROLES.ACCOUNT_ADMIN,
          ACCOUNT_ROLES.ACCOUNT_MANAGER,
        ]}
        requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.BOARD_MEMBER]}
      >
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'error.main',
              mb: 1,
            }}
          >
            Danger Zone
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1.5, lineHeight: 1.5 }}>
            {isDraft
              ? 'Permanently delete this draft. This cannot be undone.'
              : 'Removes this announcement from the app for all members. Emails already sent cannot be recalled.'}
          </Typography>
          <Button
            color="error"
            fullWidth
            onClick={() => setDeleteOpen(true)}
            size="small"
            startIcon={<TrashIcon size={14} />}
            variant="outlined"
          >
            {isDraft ? 'Delete Draft' : 'Remove from Feed'}
          </Button>
        </Box>
      </RoleGuard>

      <Dialog
        onClose={() => !isDeleting && setDeleteOpen(false)}
        open={deleteOpen}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 440, width: '100%', m: 2 } }}
      >
        <Box sx={{ p: 3 }}>
          {/* Icon badge */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'error.lighter',
              color: 'error.main',
              mb: 2,
            }}
          >
            <WarningIcon size={24} weight="fill" />
          </Box>

          {/* Title */}
          <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 }}>
            {isDraft ? 'Delete this draft?' : 'Remove this announcement?'}
          </Typography>

          {/* Subject context */}
          {announcement?.subject && (
            <Typography color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500, mt: 0.5 }}>
              “{announcement.subject}”
            </Typography>
          )}

          {/* Body */}
          <Typography color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6, mt: 1.5 }}>
            {isDraft
              ? 'This draft was never sent and will be permanently deleted. This action cannot be undone.'
              : 'This removes the announcement from the app for everyone in the community. This action cannot be undone.'}
          </Typography>

          {/* Email-already-sent callout (published with email) */}
          {!isDraft && announcement?.send_email && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#F59E0B14',
                border: '1px solid #F59E0B33',
                alignItems: 'flex-start',
              }}
            >
              <EnvelopeIcon size={16} color="#D97706" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
                Emails already sent to members <strong>cannot be recalled</strong> — only the in-app copy is removed.
              </Typography>
            </Stack>
          )}

          {/* Actions */}
          <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: 'flex-end' }}>
            <Button color="inherit" disabled={isDeleting} onClick={() => setDeleteOpen(false)} sx={{ color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button
              color="error"
              disableElevation
              disabled={isDeleting}
              onClick={handleDelete}
              startIcon={isDeleting ? <CircularProgress color="inherit" size={14} /> : <TrashIcon size={14} />}
              variant="contained"
            >
              {isDraft ? 'Delete Draft' : 'Remove from Feed'}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Stack>
  );
}
